import { TextInput } from "components/Inputs";
import { Button, LoadingState } from "components/Shared";
import parse from "html-react-parser";
import Datasource, { DatasourceResponse } from "lib/datasource";
import { setMergedStateWrapper } from "lib/hooks";
import _ from "lodash";
import moment from "moment";
import React, { memo, useCallback, useContext, useEffect, useRef, useState } from "react";
import { CommentContext } from "./CardDynamic";
import CreateWithDocument from "./CreateWithDocument";

const ReplyToInfo = () => {
	const { context, setContext } = useContext(CommentContext);
	const setMergedContext = setMergedStateWrapper(setContext);
	const { replyToComment } = context;
	const onClear = () => setMergedContext({ replyToComment: {} });

	if (!_.get(replyToComment, "id")) return null;

	return (
		<>
			<div className="m-auto flex">
				<div className="flex-10">
					<div className={"inline-block mr-1"}>Replying to {_.get(replyToComment, "created_by.name", "")}: </div>
					<div className={"inline-block font-bold"}>{parse(replyToComment.body)}</div>
				</div>
				<div className="flex-2">
					<button type="button" className="close" aria-label="Close" onClick={onClear}>
						<span aria-hidden="true">×</span>
					</button>
				</div>
			</div>
		</>
	);
};

const SubmitButton = ({ datasource, globalId, body, metadata, textAvailable }: any) => {
	const { context } = useContext(CommentContext);
	const { replyToComment } = context;
	const onSubmit = () => {
		datasource.post(
			"v2",
			`comments`,
			{
				body: {
					global_id: globalId,
					body: body,
					reply_to_id: _.get(replyToComment, "id", null),
					metadata: metadata
				}
			},
			{ updateIsLoding: true }
		);
	};

	return <Button className={`${textAvailable ? '': 'bg-black-5 text-black-20 pointer-events-none'}`} kind="send" onClick={onSubmit} />;

};

const Create = ({ globalId, taggableUsers }: any) => {
	const { context, setContext } = useContext(CommentContext);
	const { replyToComment } = context;
	const textInputRef = useRef(null as any);
	const setMergedContext = setMergedStateWrapper(setContext);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(new Datasource({ mainModelName: "comment", renderTrigger: renderTrigger }));
	const [uploadModalOpen, setUploadModalOpen] = useState(false);

	// Read draft from the shared CommentContext (F2). draftComment and
	// draftMetadata live in context rather than local state so the upload-document
	// modal can read the same draft when it opens (acceptance criteria a & b).
	// Note: the main-box onChange (below) only writes draftComment, NOT draftMetadata.
	// Mention data is embedded inside the HTML body, not in metadata. draftMetadata
	// exists for forward-compat / document-link carry-over: the document-upload path
	// merges document ogTitle/ogUrl on top of whatever draftMetadata is in context at
	// submit time (see CreateWithDocument.tsx submitComment).
	const draftComment: string = context.draftComment ?? "";
	const draftMetadata: any = context.draftMetadata ?? {};

	const textAvailable = draftComment !== "" && draftComment !== "<p></p>";

	// contentKey is bumped whenever the upload modal transitions open→closed so
	// that the main-box DraftText editor re-hydrates from the (possibly
	// modal-edited) draftComment at that sync point (F5 / F4).
	// We do NOT bump it mid-typing — only on the modal close transition.
	const [mainEditorContentKey, setMainEditorContentKey] = useState(0);

	const onSubmitSuccess = useCallback(
		(newComment: any) => {
			setMergedContext({
				highlightCommentId: newComment.id,
				listReload: true,
				// Clear the shared draft after a successful send (criterion d).
				draftComment: "",
				draftMetadata: {}
			});
		},
		[setMergedContext]
	);

	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			if (response.success) onSubmitSuccess(response.normalizedMainModelResponse);
		});
		return () => sub.unsubscribe();
	}, [datasource, onSubmitSuccess]);

	useEffect(() => {
		if(!_.isEmpty(replyToComment)) textInputRef.current?.focus();
	}, [replyToComment, textInputRef]);

	// When the upload modal closes, bump contentKey so the main editor re-hydrates
	// from whatever draftComment the modal may have written back (F5).
	const handleUploadModalClose = useCallback(() => {
		setUploadModalOpen(false);
		setMainEditorContentKey(k => k + 1);
	}, []);

	if (datasource.isLoading) return <LoadingState />;

	return (
		<div className="new-comment-form mt-2">
			<ReplyToInfo />
			{/*
			  * contentKey drives the one-shot hydration in DraftText (F4).
			  * It is bumped only when the modal closes so the editor re-displays
			  * any text the user typed in the modal — it is NOT bumped on keystrokes
			  * so there is no mid-typing cursor clobber.
			  */}
			<TextInput
				ref={child => (textInputRef.current = child)}
				users={taggableUsers}
				body={draftComment}
				contentKey={mainEditorContentKey}
				hint="Use an @ tag followed by a person's name to mention them in a comment.
					Use the 'All' options if you want everyone on this task to be tagged in a comment."
				onChange={(newBody: string) => {
					setMergedContext({ draftComment: newBody });
				}}
				spellCheck={true}
			/>
			<button
				className="button btn btn-add btn-primary"
				type="button"
				onClick={() => setUploadModalOpen(!uploadModalOpen)}
			>
				<i className="fas fa-plus" />
			</button>
			<SubmitButton textAvailable={textAvailable} globalId={globalId} body={draftComment} metadata={draftMetadata} datasource={datasource} />
			{/*
			  * comment prop removed — the modal reads draftComment directly from
			  * CommentContext (F3). onClose uses handleUploadModalClose so the main
			  * editor re-hydrates on close (F5).
			  */}
			<CreateWithDocument
				globalId={globalId}
				users={taggableUsers}
				isOpen={uploadModalOpen}
				onClose={handleUploadModalClose}
				onSubmitSuccess={onSubmitSuccess}
			/>
		</div>
	);
};

export default memo(Create);
